import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminDashboard } from "../../store/slices/adminSlice";
import {
  FiUsers,
  FiBook,
  FiLayers,
  FiDollarSign,
  FiTrendingUp,
  FiActivity,
  FiUserCheck,
} from "react-icons/fi";
import "./Dashboard.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { dashboard, isLoading, error } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Always fetch fresh data on mount
    dispatch(fetchAdminDashboard());
  }, [dispatch]);

  // Extract stats from the backend response structure
  const userStats = dashboard?.userStats || {};
  const courseStats = dashboard?.courseStats || {};
  const batchStats = dashboard?.batchStats || {};
  const enrollmentStats = dashboard?.enrollmentStats || {};
  const paymentStats = dashboard?.paymentStats || {};
  const enquiryStats = dashboard?.enquiryStats || {};
  const lectureStats = dashboard?.lectureStats || {};

  const highlightMetrics = [
    {
      icon: FiUsers,
      label: "Total Users",
      value: userStats.totalUsers || 0,
      trend: `${userStats.byRole?.student || 0} students`,
    },
    {
      icon: FiBook,
      label: "Active Courses",
      value: courseStats.totalCourses || 0,
      trend: `${courseStats.active || 0} active`,
    },
    {
      icon: FiLayers,
      label: "Batches",
      value: batchStats.totalBatches || 0,
      trend: `${batchStats.activeBatches || 0} ongoing`,
    },
    {
      icon: FiDollarSign,
      label: "Revenue",
      value: `₹${paymentStats.totalRevenue || 0}`,
      trend: "Total revenue",
    },
  ];

  // Additional metrics
  const additionalMetrics = [
    {
      icon: FiUserCheck,
      label: "Enrollments",
      value: enrollmentStats.totalEnrollments || 0,
      trend: `${enrollmentStats.activeEnrollments || 0} active`,
    },
    {
      icon: FiActivity,
      label: "Enquiries",
      value: enquiryStats.totalEnquiries || 0,
      trend: `${enquiryStats.pendingEnquiries || 0} pending`,
    },
    {
      icon: FiTrendingUp,
      label: "Upcoming Lectures",
      value: lectureStats.upcomingLectures || 0,
      trend: "Next 7 days",
    },
  ];

  return (
    <div className="dashboard-shell">
      <section className="dashboard-hero">
        <div className="dashboard-hero__copy">
          <span className="hero-eyebrow">
            Welcome back, {user?.firstName || "Admin"}
          </span>
          <h1>Admin Dashboard</h1>
          <p className="hero-meta">Manage your platform from here</p>
        </div>
      </section>

      <section className="stats-grid">
        {highlightMetrics.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.label} className="stat-card">
              <span className="stat-card__icon">
                <Icon size={24} />
              </span>
              <div>
                <p>{stat.label}</p>
                <h2>{stat.value}</h2>
                <span>{stat.trend}</span>
              </div>
            </article>
          );
        })}
      </section>

      <section className="stats-grid secondary">
        {additionalMetrics.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.label} className="stat-card secondary">
              <span className="stat-card__icon">
                <Icon size={20} />
              </span>
              <div>
                <p>{stat.label}</p>
                <h2>{stat.value}</h2>
                <span>{stat.trend}</span>
              </div>
            </article>
          );
        })}
      </section>

      {/* Recent Activities Section */}
      {dashboard?.recentEnquiries && dashboard.recentEnquiries.length > 0 && (
        <section className="recent-section">
          <h2>Recent Enquiries</h2>
          <div className="recent-list">
            {dashboard.recentEnquiries.slice(0, 5).map((enquiry) => (
              <div key={enquiry._id} className="recent-item">
                <div>
                  <strong>{enquiry.name}</strong>
                  <span>{enquiry.email}</span>
                </div>
                <span className={`status-badge status-${enquiry.status}`}>
                  {enquiry.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {dashboard?.recentEnrollments &&
        dashboard.recentEnrollments.length > 0 && (
          <section className="recent-section">
            <h2>Recent Enrollments</h2>
            <div className="recent-list">
              {dashboard.recentEnrollments.map((enrollment) => (
                <div key={enrollment._id} className="recent-item">
                  <div>
                    <strong>{enrollment.user?.name || "Unknown"}</strong>
                    <span>{enrollment.course?.title || "N/A"}</span>
                  </div>
                  <span className={`status-badge status-${enrollment.status}`}>
                    {enrollment.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

      {isLoading && !dashboard && (
        <div className="panel-state">Loading dashboard data...</div>
      )}

      {error && (
        <div className="panel-error" role="alert">
          Unable to load dashboard: {error}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
